import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../../auth.service';
import {
  GAWorkOrderService,
  BodyWorkOrderService,
  BaseService,
  ProgramDetailsGAService
} from '../ve.service';
import {
  regionFilterForm,
  workOrderDetailsForm,
  additionalInfoForm,
  deliverablesObjectivesForm,
  gaAddMultipleForm
} from './GA/ga-work-orders.template';
import {
  bodyMainEditForm,
  bodyAddMultipleForm,
  bodyWoSubmitReportForm
} from './Body/body-work-orders.template';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { HttpParams } from '@angular/common/http';
import { cgpAuthentication } from '../../../../modules/cgp-authentication';
import { WorkOrdersFilterService } from './work-orders-filter.service';
import { CgpSnackbarService } from '../../../shared/cgp-snackbar/cgp-snackbar.service';
import { CgpConfirmationDialogService } from '../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FilterableService } from '../filterable-service';
import { BehaviorSubject, Subject } from 'rxjs';

const VE_WORK_ORDER_ROUTE = '/VE/Work-Orders';

@Injectable()
export class WorkOrdersService {
  router: Router;
  baseService: BaseService;
  userModule: any;
  formBuilder: UntypedFormBuilder;
  gaWorkOrderService: GAWorkOrderService;
  bodyWorkOrderService: BodyWorkOrderService;
  gaProgramDetailsService: ProgramDetailsGAService;
  workOrdersFilterService: WorkOrdersFilterService;
  cgpConfirmationDialogService: CgpConfirmationDialogService;
  bodyMainEditForm: UntypedFormGroup;
  bodyAddMultipleForm: UntypedFormGroup;
  bodyWoSubmitReportForm: UntypedFormGroup;
  workOrderDetailsForm: UntypedFormGroup;
  additionalInfoForm: UntypedFormGroup;
  deliverablesObjectivesForm: UntypedFormGroup;
  gaAddMultipleForm: UntypedFormGroup;
  regionFilterForm: UntypedFormGroup;
  currentUser: any;
  regionFilter: any;
  programFilter: any;
  options = {
    isShowMyWorkOrders: true,
    isShowAssigned: false,
    isShowUnAssigned: false,
  };
  matDialog: MatDialog;
  service: any;
  isAddInfoFormValid = new BehaviorSubject(false);
  isDeliverablesObjFormValid: boolean;

  private isNew = true;
  private isCopy = false;
  private isViewOnly = false;
  private isAddMultiple = false;
  private isReports = false;
  private isSearch = false;
  private status: any;
  private readonly module: any;
  workOrderStatus = new FilterableService();
  submitStatus = new FilterableService();
  editData: any;
  isReportsNew = false;
  isSearchNew = false;

  public groupcss = 'group';

  constructor(
    private readonly injector: Injector,
    private readonly authService: AuthenticationService,
    public cgpSnackbarService: CgpSnackbarService,
  ) {
    this.router = this.injector.get(Router);
    this.baseService = this.injector.get(BaseService);
    this.authService = this.injector.get(AuthenticationService);
    this.gaWorkOrderService = injector.get(GAWorkOrderService);
    this.bodyWorkOrderService = injector.get(BodyWorkOrderService);
    this.gaProgramDetailsService = injector.get(ProgramDetailsGAService);
    this.workOrdersFilterService = injector.get(WorkOrdersFilterService);
    this.cgpConfirmationDialogService = injector.get(CgpConfirmationDialogService);
    this.userModule = this.authService.getModule();
    this.formBuilder = this.injector.get(UntypedFormBuilder);
    this.matDialog = injector.get(MatDialog);

    // Body Forms
    this.bodyMainEditForm = this.formBuilder.group(bodyMainEditForm);
    this.bodyAddMultipleForm = this.formBuilder.group(bodyAddMultipleForm);
    this.bodyWoSubmitReportForm = this.formBuilder.group(bodyWoSubmitReportForm);

    // GA Forms
    this.workOrderDetailsForm = this.formBuilder.group(workOrderDetailsForm);
    this.additionalInfoForm = this.formBuilder.group(additionalInfoForm);
    this.deliverablesObjectivesForm = this.formBuilder.group(deliverablesObjectivesForm);
    this.gaAddMultipleForm = this.formBuilder.group(gaAddMultipleForm);

    this.regionFilterForm = this.formBuilder.group(regionFilterForm);

    this.currentUser = cgpAuthentication.getUserInfo();
    this.baseService.read('/common/MescCore/GlobalRegion')
      .then(x => this.workOrdersFilterService.getLookupData.Regions.next(x));
    this.service = this.userModule === 'VE_Body' ? this.bodyWorkOrderService : this.gaWorkOrderService;

    this.module = this.authService.getModule();
    this.searchStatusServices[`${this.module}`]().then(status => {
      this.workOrderStatus.next(status);
      const submitStatuses = status.filter(x => x.Description !== 'Draft' && x.Description !== 'UnAssigned');
      this.submitStatus.next(submitStatuses);
    });
  }

  /** If creating new Work Order, isNew = true; by default, isNew = true
   *  If editing existing Work Order, isNew = false
   */
  public get isNewWO(): boolean {
    return this.isNew;
  }

  /** If copying a Work Order, isCopy = true
   *  By default, isCopy = false
   */
  public get isCopyWO(): boolean {
    return this.isCopy;
  }

  /** If viewing a Work Order, isViewOnly = true
   *  By default, isViewOnly = false
   */
  public get isViewOnlyWO(): boolean {
    return this.isViewOnly;
  }

  /** If adding multiple Work Orders, isAddMultiple = true
 *  By default, isAddMultiple = false
 */
  public get getIsAddMultipleWO(): boolean {
    return this.isAddMultiple;
  }

  /** If adding multiple Work Orders, isAddMultiple = true
   *  By default, isAddMultiple = false
   */
  public set setIsAddMultipleWO(isAddMultiple: boolean) {
    this.isAddMultiple = isAddMultiple;
  }

  public get getIsReports(): boolean {
    return this.isReports;
  }

  public set setIsReports(isReports: boolean) {
    this.isReports = isReports;
  }

  public get getIsSearch(): boolean {
    return this.isSearch;
  }

  public set setIsSearch(isSearch: boolean) {
    this.isSearch = isSearch;
  }

  public get woStatus(): string {
    return this.status;
  }

  /**
     * Service object function mapper for common search endpoints between GA and Body.
     *
     * @readonly
     * @memberof WorkOrderScheduleEditService
     */
  get searchStatusServices() {
    return {
      [`VE_GA`]: () => this.gaWorkOrderService.read(`/GetWorkOrderStatuses`),
      [`VE_Body`]: () => this.bodyWorkOrderService.read(`/GetWorkOrderStatuses`),
    };
  }

  public getSearchParams(state: any, isExcel: boolean): HttpParams {
    let _sort: any = state.sort[0];
    if (!_sort || !_sort.dir) {
      _sort = { dir: 'desc', field: 'WorkOrderNumber' };
    }
    const _pageNumber = state.skip / state.take + 1;

    return new HttpParams()
      .set('orderBy', _sort.field)
      .set('sortOrder', `${_sort.dir}`)
      .set('pageSize', `${state.take}`)
      .set('pageNumber', `${_pageNumber}`)
      .set('export', isExcel.toString())
      .set('timezoneOffSet', new Date().getTimezoneOffset().toString());
  }

  /**
   * @param force
   * @param gridState
   */
  getData(gridState: any = null, isWODirectView = false): Promise<any> {
    return this.search(gridState, this.userModule === 'VE_Body', isWODirectView);
  }

  exportToExcel = (gridState: any, columns: any) => {
    const d = new Date();
    const fileType = '.xlsx';
    const params = this.getSearchParams(gridState, true);
    const path = this.options.isShowMyWorkOrders ? '/GetInbox' : '/Search';
    const fileName = [
      'VE',
      `${this.userModule === 'VE_Body' ? 'Body' : 'GA'}WorkOrders`,
      (d.getMonth() + 1),
      d.getDate(),
      d.getFullYear()
    ].join('_') + fileType;

    const filters = this.getFilters(gridState);
    filters.PropertyNames = columns.length > 0 ? columns : [];

    this.service.export(filters, path, params)
      .then(response => {
        const blob = new Blob([response], { type: 'application/vnd.ms-excel' });
        const nav = (window.navigator as any);
        if (typeof navigator !== 'undefined' && nav.msSaveOrOpenBlob) {
          nav.msSaveOrOpenBlob(blob, fileName);
        } else {
          this.downloadFile(blob, fileName);
        }
      });
  }

  editBodyWO(dataItem: any, isCopy = false, isViewOnly = false) {
    const route = this.buildWorkOrderRoute();
    this.status = dataItem.Status;

    this.editData = dataItem;
    this.isNew = this.isCopy;

    if (isViewOnly) {
      this.disableForm();
    }

    this.loadBodyFormData(dataItem, route);
  }

  editGAWO(dataItem: any, isCopy = false, isViewOnly = false): Promise<any> {
    const route = this.buildWorkOrderRoute();
    this.status = dataItem.Status;

    this.editData = dataItem;
    this.isNew = this.isCopy;

    if (isViewOnly) {
      this.disableForm();
    }

    return this.loadGAFormData(dataItem, route);
  }

  copyGAWO(dataItem: any): Promise<any> {
    this.isCopy = true;
    return this.editGAWO(dataItem, this.isCopy);
  }

  copyBodyWO(dataItem: any) {
    this.isCopy = true;
    this.editBodyWO(dataItem, this.isCopy);
  }

  viewBodyWO(dataItem: any) {
    this.isViewOnly = true;
    this.editBodyWO(dataItem, false, this.isViewOnly);
  }

  viewGAWO(dataItem: any) {
    this.isViewOnly = true;
    this.editGAWO(dataItem, false, this.isViewOnly);
  }

  /**
   * @param workOrder
   * @param isCancel
   * Handles backward navigation from the Work Order Form to the Work Order Grid.
   * Backward navigation occurs when the user 1) clicks the back arrow, 2) clicks Cancel
   * from the Work Order Form
   */
  back(workOrder: any, isCancel: boolean) {
    const titleMsg = isCancel ? 'Confirm Cancel' : 'Confirm Navigation';
    const msg = isCancel
      ? 'Are you sure you want to Cancel? Your changes may be lost. Note: If you removed file(s), they will remain deleted.'
      : 'Are you sure you want to navigate away from this page? Your changes may be lost. Note: If you removed file(s), they will remain deleted.';

    this.cgpConfirmationDialogService.showConfirmationDialog({
      title: titleMsg,
      message: msg,
      alert: 'danger',
      onYesClick: () => {
        // if editing an existing work order,
        // clear form and navigate back to grid;
        // do not delete the existing work order
        if (!this.isNew) {
          this.router.navigate([this.workOrderGridRouting[this.getWorkOrderGridType()]()]);
        }
        // if creating a new work order or a new copy, 
        // then delete the newly created item before
        // navigating back to grid
        else if (this.isNew || this.isCopy) {
          this.service.loading.next(true);
          this.service.remove('/' + workOrder.Id, true).then(response => {
            if (response.status === 200) {
              this.service.loading.next(false);
              this.router.navigate([this.workOrderGridRouting[this.getWorkOrderGridType()]()]);
            }
          });
        }
        this.cgpConfirmationDialogService.closeConfirmationDialog();
      },
      onNoClick: () => {
        this.cgpConfirmationDialogService.closeConfirmationDialog();
      }
    });
  }

  /**
   * @param action - button action, either 'save' or 'submit'
   * @param workOrder
   * Save or Submit Work Order for either GA or Body
   */
  saveOrSubmit(action: string, workOrder: any): Promise<any> {
    let path: string;
    if (this.userModule === 'VE_Body') {
      // Save or Submit API path for Body WO
      path = action === 'save' ? '/Save/' : '/Submit/';
    } else {
      // Save or Submit API path for GA WO
      path = action === 'save' ? '/Save?workOrderId=' : '/Submit?workOrderId=';
    }

    this.service.loading.next(true);

    return this.service.save(workOrder, false, path + workOrder.Id)
      .then(response => {
        this.service.loading.next(false);
        this.displaySnackbar(response, action);
      });
  }

  /** Reset filters set by either Reports or Search */
  resetFilters() {
    if (this.getIsReports) {
      this.workOrdersFilterService.programs = null;
    }
    if (this.getIsSearch) {
      this.workOrdersFilterService.searchCriteria = null;
    }
  }

  resetGridView() {
    this.options = {
      isShowMyWorkOrders: true,
      isShowAssigned: false,
      isShowUnAssigned: false,
    };
  }

  resetForm() {
    this.isNew = true;
    this.isCopy = false;
    this.isViewOnly = false;
    this.isAddMultiple = false;

    if (this.userModule === 'VE_Body') {
      this.bodyMainEditForm.reset();
      if (this.bodyAddMultipleForm) {
        this.bodyAddMultipleForm.reset();
      }
    } else {
      this.workOrderDetailsForm.reset();
      this.additionalInfoForm.reset();
      this.deliverablesObjectivesForm.reset();
      if (this.gaAddMultipleForm) {
        this.gaAddMultipleForm.reset();
      }
    }
  }

  enableForm() {
    if (this.userModule === 'VE_Body') {
      this.bodyMainEditForm.enable();
    } else {
      this.workOrderDetailsForm.enable();
      this.additionalInfoForm.enable();
      this.deliverablesObjectivesForm.enable();
    }
  }

  disableForm() {
    if (this.userModule === 'VE_Body') {
      this.bodyMainEditForm.disable();
    } else {
      this.workOrderDetailsForm.disable();
      this.additionalInfoForm.disable();
      this.deliverablesObjectivesForm.disable();
    }
  }

  /**
   * @param response
   * @param action - accepted values: 'submit', 'save', 'delete', 'bulk-delete', 'copy', 'cancel-copy', 'submit-report'
   */
  displaySnackbar(response: any, action: string) {
    // Display message in snackbar
    this.cgpSnackbarService.showSnackbar({
      message: this?.buildSnackbarMessage(response, action),
      duration: 5000,
    });

    // Navigate back to work order grid
    if (response?.status === 200) {
      this.router.navigate([this.workOrderGridRouting[this.getWorkOrderGridType()]()]);
    }
  }

  private buildSnackbarMessage(response: any, action: string): string {
    switch (action.toLowerCase()) {
      case 'submit':
        return response.status === 200
          ? 'Work Order submitted successfully.'
          : 'Error: Work Order not submitted.';

      case 'save':
        return response.status === 200
          ? 'Work Order changes saved successfully.'
          : 'Error: Work Order not saved.';

      case 'delete':
        return response.status === 200
          ? 'Work Order deleted successfully.'
          : 'Error: Work Order not deleted.';

      case 'bulk-delete':
        return response.status === 200
          ? 'Work Orders deleted successfully.'
          : 'Error: Work Orders not deleted.';

      case 'copy':
        return response
          ? 'Work Order copied successfully.'
          : 'Error: Work Order not copied.';

      case 'cancel-copy':
        return response.status === 200
          ? 'Work Order copy discarded.'
          : 'Error: Work Order copy not discarded.';

      case 'submit-report':
        return response.status === 200
          ? 'Report submitted successfully.'
          : 'Error: Report not submitted.';

      default:
        return 'Error: No action taken. Please try again.';
    }
  }

  private search(gridState: any, isBodyData: boolean, isWODirectView = false): Promise<any> {
    const service = isBodyData
      ? this.bodyWorkOrderService
      : this.gaWorkOrderService;

    const searchDto = this.getFilters(gridState);
    const searchParams = this.getSearchParams(gridState, false);

    let response: any;
    if (isWODirectView) {
      response = service.search(searchDto, '/Search', true, searchParams);
    } else {
      response = this.options.isShowMyWorkOrders
        ? service.search(searchDto, '/GetInbox', true, searchParams)
        : service.search(searchDto, '/Search', true, searchParams);
    }

    return response
      .then(x => {
        if (!x) {
          return null;
        }
        this.workOrdersFilterService.lookupDataObjects =
          x.BodyWOSearchLookupData ||
          x.GAWoSearchLookupData;

        this.workOrdersFilterService.mapLookupData(x.BodyWOSearchLookupData || x.GAWoSearchLookupData);
        const data = this.mapDates(x.WorkOrders);

        return {
          data,
          total: x.RecordCount
        } as GridDataResult;
      });
  }


  private mapDates(data: any[]): any[] {
    return data.map(x => {
      Object.keys(x).forEach(k => {
        if (k.toLowerCase().endsWith('date')) {
          x[k] = x[k] ? new Date(x[k]) : '';
        }
      });
      return x;
    });
  }

  /**
 * Map and return filters from gridState
 */
  private getFilters(gridState: any) {
    const _filters = this.userModule === 'VE_Body'
      ? this.workOrdersFilterService.mapBodyFilters(gridState.filter)
      : this.workOrdersFilterService.mapGAFilters(gridState.filter);

    if (this.regionFilter) {
      _filters.GlobalRegionId = this.regionFilter.length > 0 ? this.regionFilter : null;
    }

    if (this.isReports) {
      return this.workOrdersFilterService.getReportFilters(_filters);
    } else if (this.isSearch) {
      return this.workOrdersFilterService.getSearchFilters(_filters, this.userModule);
    } else {
      _filters.AssignedOnly = this.options.isShowAssigned;
      _filters.UnAssignedOnly = this.options.isShowUnAssigned;
    }

    return _filters;
  }

  private loadGAFormData(dataItem: any, route: any): Promise<any> {
    const modifiedDataItem = this.mapDataToForm(dataItem);

    if (modifiedDataItem.ProgramId) {
      return this.gaProgramDetailsService.read('/programid/' + modifiedDataItem.ProgramId)
        .then(response => {
          modifiedDataItem.ProgramDetailId = response[0].Id;
          this.workOrderDetailsForm.setValue(modifiedDataItem);
          this.router.navigate([route]);
        });
    } else {
      modifiedDataItem.ProgramDetailId = null;
      this.workOrderDetailsForm.setValue(modifiedDataItem);
      this.router.navigate([route]);
      return null;
    }
  }

  private loadBodyFormData(dataItem: any, route: any): Promise<any> {
    const modifiedDataItem = this.mapDataToForm(dataItem);
    modifiedDataItem.totalmBomSelected = modifiedDataItem.MbomId ? 'mBom' : 'Total Vehicle Number';
    this.bodyMainEditForm.setValue(modifiedDataItem);
    this.router.navigate([route]);
    return;
  }

  /**
   * @param blob
   * @param fileName
   * Download excel file
   */
  private downloadFile(blob: Blob, fileName: string) {
    const a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * @param dataItem - Work Order object
   * Map the parameters from the Work Order object to the Work Order Form;
   * remove parameters that are in the Work Order object but not in the Work Order Form
   */
  private mapDataToForm(dataItem: any): any {
    const form = this.userModule === 'VE_Body'
      ? this.bodyMainEditForm.value
      : this.workOrderDetailsForm.value;

    const dataItemKeys = Object.keys(dataItem);
    const formKeys = Object.keys(form);

    for (let i = 0; i < dataItemKeys.length; i++) {
      const index = formKeys.indexOf(dataItemKeys[i]);
      if (index === -1) {
        delete dataItem[dataItemKeys[i]];
      }
    }

    return dataItem;
  }

  /**
   * Build Work order route based on work order grid status and module.
   *
   * @returns {*}
   * @memberof WorkOrdersService
   */
  public buildWorkOrderRoute(add = false, addMultiple = false): any {
    const module = this.module === 'VE_Body' ? `Body` : 'Ga';

    if (this.isCopy) {
      return `${this.workOrderGridRouting[this.getWorkOrderGridType()]()}${module}-Wo-Copy`;
    } else if (this.isViewOnly) {
      return `${this.workOrderGridRouting[this.getWorkOrderGridType()]()}${module}-Wo-View`;
    } else if (add) {
      return `${this.workOrderGridRouting[this.getWorkOrderGridType()]()}${module}-Wo-Add`;
    } else if (addMultiple) {
      return `${this.workOrderGridRouting[this.getWorkOrderGridType()]()}${module}-Wo-Add-Multiple`;
    } else {
      return `${this.workOrderGridRouting[this.getWorkOrderGridType()]()}${module}-Wo-Edit`;
    }
  }

  /**
   * Get the current work order grid base route given a grid status of Search, Reports, or Default.
   *
   * @readonly
   * @memberof WorkOrdersService
   */
  get workOrderGridRouting() {
    return {
      [`Search`]: () => `/VE/Work-Orders/Search/`,
      [`Reports`]: () => `/VE/Work-Orders/Reports/`,
      [`Default`]: () => `/VE/Work-Orders/`
    };
  }

  /**
   * Return what type the grid is showing. Possible types are Search, Reports, or Default
   *
   * @returns
   * @memberof WorkOrdersService
   */
  public getWorkOrderGridType() {
    if (this.isSearch) {
      return `Search`;
    } else if (this.isReports) {
      return `Reports`;
    }
    return 'Default';
  }
}
